// TODO move to streams/
import { FormikProps } from 'formik';
import { TFunction } from 'i18next';
import { v4 as uuidv4 } from 'uuid';
import { apiClientWithoutHandler } from 'src/js/api';
import { createWatchStore, updateWatchStore } from 'src/js/store/use-watch';

import { APIPath, APIPathWithArguments } from 'src/js/api/route-path-index';
import { convertCSVStringToData } from 'src/js/helper/csv-helper';
import { StreamSrtStatsResponse } from 'src/js/model/api/response/stream-srt-stats-response';
import { NotificationVariant } from 'src/js/notification/notification';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import {
  GetAllStreamsResponse,
  SRTMode,
  SrtPathRedundancyMode,
  StreamEncapsulation,
  StreamResponse,
  StreamViewModel,
} from 'src/js/pages/streaming/stream/stream-models';
import { mapStreamRequest, mapStreamViewModel } from 'src/js/pages/streaming/stream/stream-view-model-mapper';
import { isNilOrEmpty } from 'src/js/util/global-util';
import videoEncoders from '../video-encoder/video-encoder-store';
import audioEncoders from '../audio-encoder/audio-encoder-store';

// values will be inited from API at runtime
const streams: StreamViewModel[] = [];
createWatchStore(streams);
export default streams;

export const saveResponseToStreams = (response: StreamViewModel[]) => {
  updateWatchStore(streams, response);
};

const replaceViewModel = (response: StreamResponse, t: TFunction, selected = false) => {
  const viewModel = mapStreamViewModel(response, t);
  const newList = [...streams];
  const index = newList.findIndex((e) => e.id === viewModel.id);
  if (index > -1) {
    newList.splice(index, 1, { ...viewModel, selected: selected, hidden: false, updated: null });
  }
  saveResponseToStreams(newList);
};

export const addViewModel = (response: StreamResponse, t: TFunction, selected = false) => {
  const viewModel = mapStreamViewModel(response, t);
  const newList = [...streams];
  const index = newList.findIndex((e) => e.id === viewModel.id);
  if (index !== -1) {
    /* eslint-disable no-console */
    console.error('model is already on list', viewModel.id);
  }
  newList.splice(0, 0, { ...viewModel, selected: selected, hidden: false, updated: null });
  saveResponseToStreams(newList);
};

export const removeStreamViewModel = (id: number) => {
  const newList = [...streams];
  const index = newList.findIndex((e) => e.id === id);
  if (index > -1) {
    newList.splice(index, 1);
  }
  saveResponseToStreams(newList);
};

export const validateHostname = (
  formik: any,
  hostname: string,
  fieldName: string,
  showRedText: boolean,
  t: TFunction,
  callback: any,
): Promise<any> => {
  let isValid = true;
  const path = APIPath.hostnamevalidate.stream;
  return apiClientWithoutHandler.genericController.post(path, { hostname: hostname }).then((res: any) => {
    // notification
    if (res.ok) {
      const response = res.data;
      if (response != null && response.success == false) {
        // Success false means we need to print an warning or error
        let notificationType = NotificationVariant.WARNING;
        let message = response.message ?? 'Please correct';
        if (response.message && response.message.startsWith("Failed to resolve host name '")) {
          // crop the ending hostname
          message = t('error.failedToResolve');
        }
        if (response.error === true) {
          notificationType = NotificationVariant.ERROR;
          isValid = false;
          formik.setErrors({ [fieldName]: message });
        } else if (showRedText === true) {
          const data = { [fieldName]: message };
          formik.setErrors(data);
        }

        if (response.message != null) {
          createAndDispatchNotification(response.message, notificationType, t);
        } else {
          createAndDispatchNotification(t('error.Unable to resolve destination address'), notificationType, t);
        }
      }
      callback?.(isValid);
    } else {
      createAndDispatchNotification(t('error.Failed to validate stream address'), NotificationVariant.ERROR, t);
      callback?.(false); // keep going anyway, dont stop stream creation for validation api problems.
    }
    return res;
  });
};

export const fetchStreams = (t: TFunction) => {
  return apiClientWithoutHandler.genericController.get<GetAllStreamsResponse>(APIPath.streams.list).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        const streamsViewModels = response.data?.map((stream: StreamResponse) => mapStreamViewModel(stream, t));
        // splice in codecAlgorithm from videnc if available
        streamsViewModels.forEach((stream) => {
          const videoEncoder = videoEncoders.find((v) => (stream.video ? v.id === stream.video[0]?.id : false));
          if (videoEncoder) {
            if (stream.codecAlgorithm !== t(`videnc.enums.codecAlgorithmShort.${videoEncoder.codecAlgorithm}`)) {
              stream.codecAlgorithm = t(`videnc.enums.codecAlgorithmShort.${videoEncoder.codecAlgorithm}`);
            }
            stream.videoOutputResolution = videoEncoder?.outputResolution;
            stream.videoBitrate = videoEncoder?.bitrate ? `${videoEncoder?.bitrate}kbps` : '';
          }
          const audioEncoder = audioEncoders.find((a) => (stream.audio ? a.id === stream.audio[0]?.id : false));
          if (audioEncoder) {
            stream.audioBitrate = audioEncoder?.bitRate ? `${audioEncoder?.bitRate}kbps` : '';
          }
        });
        saveResponseToStreams(streamsViewModels);
      }
    }
    return res;
  });
};

export const addStream = async (
  formikContext: FormikProps<any>,
  model: StreamViewModel,
  t: TFunction,
): Promise<any> => {
  let abortRequest = false;
  const path = APIPath.streams.list;
  const request = mapStreamRequest(model);
  const showRedTextOnHostnameValidateWarnings = false; // not showing because the form is refreshed after submit
  request.userData = uuidv4();

  if (
    model.encapsulation === StreamEncapsulation.SRT &&
    model.srtMode === SRTMode.CALLER &&
    model.srtRedundancyMode != SrtPathRedundancyMode.NONE
  ) {
    // Validate two address fields with REST API
    const response = await validateHostname(
      formikContext,
      model.address,
      'address',
      showRedTextOnHostnameValidateWarnings,
      t,
      null,
    );
    if (response.error) {
      abortRequest = true;
    }
    const response2 = await validateHostname(
      formikContext,
      model.srtRedundancyPath2Address,
      'srtRedundancyPath2Address',
      showRedTextOnHostnameValidateWarnings,
      t,
      null,
    );
    if (response2.error) {
      abortRequest = true;
    }
  } else if (model.encapsulation !== StreamEncapsulation.SRT || model.srtMode !== SRTMode.LISTENER) {
    const response = await validateHostname(
      formikContext,
      model.address,
      'address',
      showRedTextOnHostnameValidateWarnings,
      t,
      null,
    );
    if (response.error) {
      abortRequest = true;
    }
  }

  if (abortRequest) {
    return { ok: false };
  }

  return apiClientWithoutHandler.genericController
    .post<StreamResponse>(path, request)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          addViewModel(response, t);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(
        res,
        isNilOrEmpty(res.data?.info?.name)
          ? t('stream.notifications.addSuccessEmptyName')
          : t('stream.notifications.addSuccess', { name: res.data?.info?.name }),
        isNilOrEmpty(res.data?.info?.name)
          ? t('stream.notifications.addErrorEmptyName')
          : t('stream.notifications.addError', { name: res.data?.info?.name }),
        t,
      );
      return res;
    });
};

export const updateStream = (model: StreamViewModel, t: TFunction) => {
  const path = APIPathWithArguments(APIPath.streams.single, { id: model.id });
  const request = mapStreamRequest(model);
  //console.log('updateStream');
  if (request.userData === '') {
    request.userData = uuidv4(); // upgrade stream with unique ID
  }
  return apiClientWithoutHandler.genericController
    .put<StreamResponse>(path, request)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(
        res,
        isNilOrEmpty(res.data?.info?.name)
          ? t('stream.notifications.updateSuccessEmptyName')
          : t('stream.notifications.updateSuccess', { name: res.data?.info?.name }),
        isNilOrEmpty(res.data?.info?.name)
          ? t('stream.notifications.updateErrorEmptyName')
          : t('stream.notifications.updateError', { name: res.data?.info?.name }),
        t,
      );
      return res;
    });
};

export const fetchStreamSRTGraph = (
  model: StreamViewModel,
  interval: string,
  redundancyPath?: number,
  setter?: (values: StreamSrtStatsResponse[]) => void,
) => {
  return apiClientWithoutHandler.genericController
    .get<string>(APIPathWithArguments(APIPath.streams.stats, { id: model.id }), {
      interval: interval,
      type: 'srt',
      redundancyPath: redundancyPath,
    })
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        const array = convertCSVStringToData<StreamSrtStatsResponse>(response);
        if (array) {
          setter(array);
        }
      } else {
        setter([]);
      }
      return res;
    });
};

export const fetchStreamStatistics = (
  model: Partial<StreamViewModel>,
  t: TFunction,
  setStatistics?: (values: StreamViewModel) => void,
) => {
  return apiClientWithoutHandler.genericController
    .get<StreamResponse>(APIPathWithArguments(APIPath.streams.stats, { id: model.id }))
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        setStatistics?.(mapStreamViewModel(response, t));
      }
      return res;
    });
};

export const resetStreamStatistics = (
  model: StreamViewModel,
  t: TFunction,
  setStatistics?: (values: StreamViewModel) => void,
) => {
  return apiClientWithoutHandler.genericController
    .delete<StreamResponse>(APIPathWithArguments(APIPath.streams.stats, { id: model.id }), {})
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        setStatistics?.(mapStreamViewModel(response, t));
      }
      return res;
    });
};

export const startStopStream = (model: StreamViewModel, start: boolean, t: TFunction) => {
  const path = APIPathWithArguments(start ? APIPath.streams.start : APIPath.streams.stop, {
    id: model.id,
  });
  return apiClientWithoutHandler.genericController
    .put<StreamResponse>(path)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(res, null, null, t);
      return res;
    });
};

export const deleteStream = (model: StreamViewModel) => {
  const path = APIPathWithArguments(APIPath.streams.single, {
    id: model.id,
  });
  return apiClientWithoutHandler.genericController.delete(path).then((res) => {
    if (res.ok) {
      removeStreamViewModel(model.id);
    }
    return res;
  });
};
